import React, { useMemo, useState } from 'react';
import Intl from '@fx-ui/intl';
import {
    filter,
    getCurrentSuiteId,
    getResolvedSuiteViews,
    includes,
    isEmpty,
    isMobileUA,
    localStorageManager,
    redirect,
    size
} from '~/utils';
import { LOCAL_STORAGE, LOCAL_STORAGE_MIGRATE, SuiteId } from '~/constants';
import { InvitationInfo, LoginType } from '~/interfaces';
import { defaultEmail, defaultPhone } from '~/mixins/account/utils/invite';
import {
    ACCOUNT_LOGIN,
    LOGIN_TYPES_WITHOUT_SUITE_ID,
    NORMAL_LOGIN_ALLOW_CACHE_TYPES
} from '../../constants';
import { getLoginTitle, getAllowSelectedSuites, isPhoneCodeLoginCache } from '../../utils/login';
import AccountBackButton from '../../components/account-back-button';
import WeChatLogin from '../../components/wechat-login';
import WxWorkLogin from '../../components/wxwork-login';
import CorpUrlLogin from '../../components/corp-url-login';
import AccountLogin from '../account-login';
import { getBrandInfo } from '~/utils/brand';
import { accountStore } from '~/models';
import {
    SuiteSelectList,
    SuiteSelectListProps
} from '~/mixins/account/containers/suite-select-list';

const isAccountLogin = (loginType: LoginType) => {
    return ACCOUNT_LOGIN.includes(loginType);
};

const getLoginItems = () => {
    const loginItems: LoginType[] = getBrandInfo().loginTypes;

    const otherLoginItems = filter(loginItems, (loginType) => {
        // 过滤掉账户登录类型和Google登录
        if (isAccountLogin(loginType) || loginType === LoginType.Google) {
            return false;
        }
        // 移动端不展示第三方登录入口
        return !isMobileUA();
    });

    return { loginItems, otherLoginItems };
};

const { loginItems, otherLoginItems } = getLoginItems();
const defaultLoginName = defaultPhone || defaultEmail;

interface NormalLoginProps {
    accountPrefix?: React.ReactNode;
    showRegisterSwitch?: boolean;
    showForgetPassword?: boolean;
    suiteId?: SuiteId;
    onRegister?: (invitations?: InvitationInfo[]) => void;
    onComplete?: () => void;
}

/**
 * 普通登录页
 * @param props
 */
const NormalLogin: React.FC<NormalLoginProps> = (props) => {
    const {
        accountPrefix,
        showRegisterSwitch,
        showForgetPassword,
        suiteId,
        onRegister,
        onComplete
    } = props;

    const [loginType, setLoginType] = useState<LoginType>(() => {
        if (defaultLoginName) {
            return LoginType.AccountPassword;
        }
        const cachedLoginType = localStorageManager.getItemWithMigrate({
            oldKey: LOCAL_STORAGE.SIGN_IN.TYPE,
            newKey: LOCAL_STORAGE_MIGRATE.SIGN_IN_TYPE
        });

        if (
            !cachedLoginType ||
            !includes(loginItems, cachedLoginType) ||
            !includes(NORMAL_LOGIN_ALLOW_CACHE_TYPES, cachedLoginType)
        ) {
            return loginItems[0];
        }
        const hasSuiteId = getCurrentSuiteId() !== SuiteId.PLATFORM;
        const canRestoreWithoutSuite = LOGIN_TYPES_WITHOUT_SUITE_ID.includes(cachedLoginType);
        return hasSuiteId || canRestoreWithoutSuite ? cachedLoginType : loginItems[0];
    });
    const [selectSuiteStatus, setSelectSuiteStatus] = useState<{
        visible?: boolean;
        suites?: SuiteSelectListProps['items'];
    }>();
    const [back2SuiteSelectVisible, setBack2SuiteSelectVisible] = useState(false);

    const suiteViews = useMemo(() => getResolvedSuiteViews(), []);

    const getSuitesInfo = (suites: { suite_id: number; [key: string]: any }[]) => {
        return suites?.map((item) => {
            const suiteView = suiteViews[item.suite_id];
            return {
                ...item,
                suiteId: item.suite_id,
                name: suiteView?.name,
                icon: suiteView?.icon
            };
        });
    };

    const handleLoginTypeChange = (nextLoginType: LoginType, needCache?: boolean) => {
        const suites = getAllowSelectedSuites(nextLoginType);
        if (size(suites) > 1) {
            // 先选择要登录的产品，再跳转登录
            setSelectSuiteStatus({
                visible: true,
                suites: getSuitesInfo(suites!)
            });
        }
        setLoginType(nextLoginType);
        if (needCache) {
            localStorageManager.set(LOCAL_STORAGE_MIGRATE.SIGN_IN_TYPE, nextLoginType);
        }
    };

    const renderLoginContent = () => {
        if (selectSuiteStatus?.visible && !isEmpty(selectSuiteStatus?.suites)) {
            return (
                <SuiteSelectList
                    className="suite-select"
                    title={getLoginTitle(loginType)}
                    description={Intl.get('fxp.base.workspace.login.please_select_prod.tip')}
                    items={selectSuiteStatus.suites!}
                    onSelect={(suite) => {
                        if (suite.url) {
                            redirect(suite.url);
                            return;
                        }
                        if (loginType === LoginType.Wxwork) {
                            accountStore.setWxWorkLoginSuite(suite.suiteId, {
                                memberConfig: suite.member_config,
                                memberUrl: suite.member_url,
                                adminUrl: suite.admin_url
                            });
                            setSelectSuiteStatus((prev) => {
                                return {
                                    ...prev,
                                    visible: false
                                };
                            });
                            setBack2SuiteSelectVisible(true);
                        }
                    }}
                />
            );
        }
        switch (loginType) {
            case LoginType.Account:
            case LoginType.AccountPassword:
            case LoginType.PhoneCode: {
                return (
                    <AccountLogin
                        loginType={loginType}
                        setLoginType={handleLoginTypeChange}
                        otherLoginItems={otherLoginItems}
                        prefix={accountPrefix}
                        showRegisterSwitch={showRegisterSwitch}
                        showForgetPassword={showForgetPassword}
                        defaultLoginName={defaultLoginName}
                        suiteId={suiteId}
                        onRegister={onRegister}
                        onComplete={onComplete}
                    />
                );
            }
            case LoginType.Wechat: {
                return <WeChatLogin />;
            }
            case LoginType.Tenant: {
                return <CorpUrlLogin />;
            }
            case LoginType.Wxwork: {
                return <WxWorkLogin />;
            }
        }
    };

    const resetSuiteSelection = () => {
        if (accountStore.hasSelectedSuite) {
            accountStore.resetSuiteSelection();
        }
    };

    const renderBackToSuiteSelect = () => {
        return (
            <AccountBackButton
                name={Intl.get('fxp.base.workspace.login.prod_selected.ttl')}
                onClick={() => {
                    setSelectSuiteStatus((prev) => {
                        return {
                            ...prev,
                            visible: true
                        };
                    });
                    setBack2SuiteSelectVisible(false);
                    resetSuiteSelection();
                }}
            />
        );
    };

    return (
        <div className="fx-normal-login">
            {back2SuiteSelectVisible && renderBackToSuiteSelect()}
            {!back2SuiteSelectVisible && !isAccountLogin(loginType) && size(loginItems) > 1 && (
                <AccountBackButton
                    onClick={() => {
                        setLoginType(
                            isPhoneCodeLoginCache()
                                ? LoginType.PhoneCode
                                : LoginType.AccountPassword
                        );
                        localStorageManager.set(
                            LOCAL_STORAGE_MIGRATE.SIGN_IN_TYPE,
                            LoginType.Account
                        );
                        setSelectSuiteStatus(undefined);
                    }}
                />
            )}
            {renderLoginContent()}
        </div>
    );
};

export default NormalLogin;
